/* Confirm dialog helper */

import { refs, state } from './state.js';
import { api } from './api.js';

export function confirm(title, message) {
  return new Promise((resolve) => {
    refs.confirmTitle.textContent = title;
    refs.confirmMessage.textContent = message;
    // Per-call ownership: the resolver rides on the dialog element. Handlers
    // (main.js) claim-and-null it before resolving, and the 'close' handler
    // skips stale close events (dialog already reopened), so a previous
    // dialog's queued 'close' can never resolve this call's promise.
    refs.confirmDialog._currentResolve = resolve;
    refs.confirmDialog.showModal();
  });
}

/**
 * Typed-confirm gate: like confirm(), but OK stays disabled until the user
 * types `token` exactly (trimmed) into the input -- used for consequential
 * actions where a plain click-through confirm isn't enough friction (e.g. a
 * PROD-target Timeline revert). The server-side gate this satisfies is
 * authoritative regardless of what this dialog does (see timeline-view.js's
 * _onRevert); this only adds a client-side consent step in front of it.
 * Same per-call resolver-on-the-element ownership as confirm() above --
 * main.js's handlers claim-and-null it with the same stale-close guard.
 */
export function confirmTyped(title, message, token) {
  return new Promise((resolve) => {
    const d = refs.confirmTypedDialog;
    refs.confirmTypedTitle.textContent = title;
    refs.confirmTypedMessage.textContent = message;
    refs.confirmTypedInput.value = "";
    refs.confirmTypedInput.placeholder = token;
    refs.confirmTypedOk.disabled = true;
    d._expectedToken = token;
    d._currentResolve = resolve;
    d.showModal();
    refs.confirmTypedInput.focus();
  });
}

// ---------------------------------------------------------------------------
// Centralized confirm copy — single source of truth for sync prompts
// ---------------------------------------------------------------------------

export function confirmPullFromCloud(name) {
  return confirm(
    "Pull from Cloud",
    `Pull "${name}" from SAP CPQ cloud? This will overwrite your local file.`,
  );
}

export function confirmPushToCloud(name, tenant) {
  const where = tenant ? `the ${tenant} SAP CPQ cloud` : "SAP CPQ cloud";
  return confirm(
    "Push to Cloud",
    `Push "${name}" to ${where}? This overwrites the remote version on the live tenant.`,
  );
}

// ---------------------------------------------------------------------------
// M12 Wave 3 additions -- Sync view's two-pane redesign: bulk header actions
// (Push N / Pull N, listing every affected file by name -- not just a count,
// per that task's explicit SAFETY instruction) + the new per-file Discard
// action. All three build on the SAME confirm() primitive as every other
// dialog in this file; nothing here talks to the network.
// ---------------------------------------------------------------------------

export function confirmPushMultiple(names, tenant) {
  const where = tenant ? `the ${tenant} SAP CPQ cloud` : "SAP CPQ cloud";
  const list = names.map((n) => `  • ${n}`).join("\n");
  return confirm(
    "Push to Cloud",
    `Push ${names.length} file(s) to ${where}? This overwrites the remote version of each on the live tenant.\n\n${list}`,
  );
}

export function confirmPullMultiple(names) {
  const list = names.map((n) => `  • ${n}`).join("\n");
  return confirm(
    "Pull from Cloud",
    `Pull ${names.length} file(s) from SAP CPQ cloud? This overwrites each local file.\n\n${list}`,
  );
}

// Discard destroys local edits -- its own explicit confirm naming the file,
// distinct from confirmPullFromCloud's copy even when the underlying action
// IS a pull under the hood (a conflict row's "discard my local changes"):
// the caller passes a `detail` sentence describing what actually happens
// (stash vs. pull-over-local) so the dialog stays accurate to the real
// mechanism instead of a one-size-fits-all "discard" claim.
export function confirmDiscardChanges(name, detail) {
  return confirm(
    "Discard Changes",
    `Discard changes to "${name}"? ${detail}`,
  );
}

// ---------------------------------------------------------------------------
// Approval Review Modal — "Review this change" before pushing to cloud
// ---------------------------------------------------------------------------

let _arvDialog = null;
// Resolvers ride on each dialog ELEMENT (dlg._currentResolve) with claim-and-null
// handlers + a stale-close guard — the hardened pattern from confirm()/confirmTyped():
// a queued 'close' event from a previous dialog can otherwise land after a reopen
// and resolve the NEW call's promise with the cancel value.

function _ensureArvDialog() {
  if (_arvDialog) return _arvDialog;
  const d = document.createElement("dialog");
  d.className = "confirm-dialog arv-dialog";
  d.style.cssText = "max-width:520px";
  d.innerHTML = `
    <div class="confirm-body arv-body">
      <h3 style="margin:0 0 14px;font-size:15px;font-weight:600;letter-spacing:-0.01em">Review this change</h3>
      <div class="arv-operation">
        <div class="art-row">
          <span class="art-label">Operation</span>
          <span class="art-value arv-op-text"></span>
        </div>
        <div class="art-row">
          <span class="art-label">Risk</span>
          <span class="art-value arv-risk-badge"></span>
        </div>
      </div>
      <div class="art-row arv-tenant-section" style="margin-top:2px">
        <span class="art-label">Tenant</span>
        <span class="art-value"><span class="arv-tenant-name"></span><span class="arv-env-badge" style="margin-left:6px"></span></span>
      </div>
      <div class="art-row arv-reversibility" style="margin-top:2px">
        <span class="art-label">Reversibility</span>
        <span class="art-value arv-rev-chip"></span>
      </div>
      <div class="art-row arv-validation" style="margin-top:2px">
        <span class="art-label">Validation</span>
        <span class="art-value arv-val-content"></span>
      </div>
      <div class="art-row arv-blast-row" style="display:none;margin-top:2px">
        <span class="art-label">Blast radius</span>
        <span class="art-value arv-blast-content"></span>
      </div>
      <p class="arv-hosted-note" style="display:none;margin:10px 0 0;font-size:12px;color:var(--text-secondary)">File/script pushes apply from the desktop app for now.</p>
      <div class="arv-typed-confirm" style="display:none;margin-top:12px">
        <p class="arv-typed-reason" style="margin:0 0 6px;font-size:12px;color:var(--text-secondary)">This push targets production. Type the tenant name to confirm:</p>
        <input type="text" class="arv-typed-input" autocomplete="off" style="width:100%;box-sizing:border-box;padding:6px 10px;border:1px solid var(--border);border-radius:8px;font-size:13px">
      </div>
      <div class="confirm-btns" style="margin-top:20px">
        <button type="button" class="btn arv-cancel-btn">Cancel</button>
        <button type="button" class="btn btn-primary arv-approve-btn">Approve</button>
      </div>
    </div>
  `;

  const cancelBtn  = d.querySelector(".arv-cancel-btn");
  const approveBtn = d.querySelector(".arv-approve-btn");

  const claim = () => { const r = d._currentResolve; d._currentResolve = null; return r; };

  cancelBtn.addEventListener("click", () => {
    const r = claim();
    d.close();
    r?.(false);
  });

  approveBtn.addEventListener("click", () => {
    const r = claim();
    d.close();
    r?.(true);
  });

  d.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !approveBtn.disabled) {
      e.preventDefault();
      approveBtn.click();
    }
  });

  d.addEventListener("close", () => {
    if (d.open) return;   // stale close from a previous dialog after a reopen
    claim()?.(false);     // Esc/backdrop: resolve false
  });

  document.body.appendChild(d);
  _arvDialog = d;
  return d;
}

function _riskBadgeHtml(level) {
  const l = (level || "").toLowerCase();
  if (l === "read")   return `<span class="rev-badge rev-ok"><span class="rev-dot"></span>Read-only</span>`;
  if (l === "delete") return `<span class="rev-badge rev-risk"><span class="rev-dot"></span>Destructive</span>`;
  const label = level ? level.charAt(0).toUpperCase() + level.slice(1) : "Mutate";
  return `<span class="rev-badge rev-risk"><span class="rev-dot"></span>${_escHtml(label)}</span>`;
}

function _revBadgeHtml(rev) {
  const l = (rev || "").toLowerCase();
  if (l === "reversible")   return `<span class="rev-badge rev-ok"><span class="rev-dot"></span>Reversible</span>`;
  if (l === "risky")        return `<span class="rev-badge rev-risk"><span class="rev-dot"></span>Risky</span>`;
  if (l === "irreversible") return `<span class="rev-badge rev-risk"><span class="rev-dot"></span>Not reversible</span>`;
  return `<span class="rev-badge"><span class="rev-dot"></span>${_escHtml(rev || "Unknown")}</span>`;
}

function _valBadgeHtml(status, messages) {
  const msgs = (messages || []).filter(Boolean);
  let cls, label;
  if (status === "pass")      { cls = "rev-badge rev-ok";   label = "Validated"; }
  else if (status === "warn") { cls = "rev-badge rev-risk"; label = "Warnings"; }
  else                        { cls = "rev-badge";           label = "Not pre-validated"; }
  const badge = `<span class="${cls}"><span class="rev-dot"></span>${_escHtml(label)}</span>`;
  if (!msgs.length) return badge;
  const items = msgs.map(m => `<li>${_escHtml(m)}</li>`).join("");
  return `<details class="arv-val-details"><summary>${badge}</summary><ul style="margin:4px 0 0 0;padding:0 0 0 14px;font-size:12px;color:var(--text-secondary)">${items}</ul></details>`;
}

function _envBadgeHtml(envRole) {
  if (!envRole) return "";
  const l = (envRole || "").toLowerCase();
  if (l === "prod") return `<span class="rev-badge rev-risk" style="font-size:10px;padding:1px 5px">PROD</span>`;
  if (l === "test") return `<span class="rev-badge" style="font-size:10px;padding:1px 5px">Test</span>`;
  return `<span class="rev-badge rev-ok" style="font-size:10px;padding:1px 5px">Dev</span>`;
}

function _populateArvDialog(dialog, manifest) {
  const op  = manifest.operation || {};
  const ten = manifest.tenant || {};
  const rev = manifest.reversibility || "irreversible";
  const val = manifest.validation || {};

  const $   = (sel) => dialog.querySelector(sel);
  const set = (sel, html) => { const el = $(sel); if (el) el.innerHTML = html; };

  set(".arv-op-text",    _escHtml(op.summary || op.title || op.tool || ""));
  set(".arv-risk-badge", _riskBadgeHtml(op.risk_level));
  set(".arv-tenant-name", _escHtml(ten.label || "(local)"));
  set(".arv-env-badge",   _envBadgeHtml(ten.env_role));
  set(".arv-rev-chip",    _revBadgeHtml(rev));
  set(".arv-val-content", _valBadgeHtml(val.status, val.messages));

  // Blast radius: show "Affects N objects" only for "all" or count > 1; single-object
  // (count 1) and empty (count 0 — not high anyway) ops show nothing.
  const br    = manifest.blast_radius || {};
  const count = br.object_count;
  const blastRow = $(".arv-blast-row");
  if (blastRow) {
    if (count === "all" || (typeof count === "number" && count > 1)) {
      const text = count === "all"
        ? "Affects all assets"
        : `Affects ${_escHtml(String(count))} objects`;
      const cls = br.is_high ? "rev-badge rev-risk" : "rev-badge";
      set(".arv-blast-content", `<span class="${cls}"><span class="rev-dot"></span>${text}</span>`);
      blastRow.style.display = "";
    } else {
      blastRow.style.display = "none";
    }
  }

  const typedSection = $(".arv-typed-confirm");
  const typedInput   = $(".arv-typed-input");
  const approveBtn   = $(".arv-approve-btn");

  // Friction is NEVER skipped: when requires_typed_confirm is true we ALWAYS show the
  // typed-confirm gate. blast_radius can flag this on a label-less local/test tenant, so
  // the expected token falls back to the literal "CONFIRM" when there's no tenant label.
  if (manifest.requires_typed_confirm) {
    typedSection.style.display = "block";
    typedInput.value = "";
    approveBtn.disabled = true;
    // Compose reason text from confirm_reason TOKENS (split on "+", exact-match — so a
    // future reason string can't accidentally substring-match an existing one).
    const reasons = (manifest.confirm_reason || "").split("+");
    const parts = [];
    if (reasons.includes("prod"))              parts.push("This is a production tenant.");
    if (reasons.includes("high_blast_radius")) parts.push("This change affects many objects.");
    const expected = ten.label || "CONFIRM";
    const typeTail = ten.label ? "Type the tenant name to confirm:" : "Type CONFIRM to confirm:";
    const reasonText = parts.length
      ? parts.join(" ") + " " + typeTail
      : "This push targets production. " + typeTail;
    const promptEl = $(".arv-typed-reason");
    if (promptEl) promptEl.textContent = reasonText;
    const check = () => { approveBtn.disabled = typedInput.value.trim() !== expected.trim(); };
    typedInput.addEventListener("input", check);
    dialog.addEventListener("close", () => typedInput.removeEventListener("input", check), { once: true });
  } else {
    typedSection.style.display = "none";
    typedInput.value = "";
    approveBtn.disabled = false;
  }

  // hosted_appliable === false: tool relies on FS access unavailable in hosted mode.
  // Show a note and lock Approve — the user must apply from the desktop app.
  const hostedNote = $(".arv-hosted-note");
  if (manifest.hosted_appliable === false) {
    if (hostedNote) hostedNote.style.display = "";
    typedSection.style.display = "none";
    approveBtn.disabled = true;
  } else {
    if (hostedNote) hostedNote.style.display = "none";
  }
}

/**
 * Show the "Review this change" approval modal before a live-cloud push.
 * Calls api.pushPreview (read-only; never executes the tool) — in hosted mode
 * this maps to /api/connections/{cid}/push/preview; in local/desktop mode it
 * maps to /api/agent/push/preview — to assemble the manifest, then renders:
 * operation summary + risk badge,
 * tenant + env badge, reversibility chip, validation badge (expandable).
 * When manifest.requires_typed_confirm is true, gates Approve behind typing
 * the tenant name (triggers only when env_role is "prod").
 * On preview fetch failure, falls back to confirmPushToCloud — never blocks
 * the push path. Returns Promise<bool>: true = Approve, false = Cancel.
 */
export async function showApprovalReviewModal(artifact, tenant) {
  const convId = state.activeSessionId || "default";
  let manifest;
  try {
    manifest = await api.pushPreview(convId, artifact);
  } catch (e) {
    console.warn("[approval] preview failed, falling back to simple confirm:", e);
    const what = (artifact && artifact.name) || "change";
    return confirmPushToCloud(what, tenant);
  }
  return new Promise((resolve) => {
    const dialog = _ensureArvDialog();
    dialog._currentResolve = resolve;
    _populateArvDialog(dialog, manifest);
    dialog.showModal();
  });
}

// ---------------------------------------------------------------------------
// Billing & usage dialog (M12 Wave 1 T8) — a small breakdown: free-tier
// status, this-period spend vs. allowance, lifetime total, and a
// per-connection table. Hosted-only (both call sites -- the v2 sidebar's
// Usage row and the account menu's "Billing & usage" item, shell-v2.js --
// gate on getAuthMode()==="hosted" before ever opening this). Follows the
// SAME "hardened dialog" pattern as _ensureArvDialog() above: a lazily
// created, module-cached <dialog> appended to document.body once, reusing
// the generic .confirm-dialog/.confirm-body/.art-row/.art-label/.art-value
// components (styles.css) rather than a bespoke stylesheet -- this is a
// plain info dialog (no resolve/reject), so no _currentResolve/stale-close
// guard is needed, only a Close button. It DOES need a different guard,
// though (fix round, MINOR-3): openBillingUsageDialog() fetches fresh on
// every open, so a rapid close->reopen can have two fetches in flight at
// once -- see the _billingGen generation-token comment just below for the
// stale-response race that guards against. Every dynamic value is set via
// .textContent (never innerHTML string interpolation), including the
// per-connection table (built with document.createElement, not a
// template-string join) -- belt-and-suspenders beyond esc()/escAttr(),
// since textContent can never parse markup regardless of content.
// ---------------------------------------------------------------------------

let _billingDialog = null;
// Per-open generation token (fix round, review finding MINOR-3): showModal()
// -> await Promise.all([...]) -> appendChild is a classic stale-async-write
// race on this module-cached, reused dialog. A rapid close -> reopen with
// out-of-order responses (the FIRST open's slow fetch resolving AFTER the
// SECOND open already started and finished) would otherwise either stack a
// second connection table on top of the first, or overwrite the second
// open's fresh text with the first open's stale text. Every
// openBillingUsageDialog() call claims the next generation; any response
// that resolves after a NEWER open has already claimed the token is a
// stale response and is discarded rather than applied.
let _billingGen = 0;

function _ensureBillingDialog() {
  if (_billingDialog) return _billingDialog;
  const d = document.createElement("dialog");
  d.className = "confirm-dialog billing-dialog";
  d.style.cssText = "max-width:480px";
  d.innerHTML = `
    <div class="confirm-body">
      <h3>Billing &amp; usage</h3>
      <div class="art-row"><span class="art-label">Plan</span><span class="art-value billing-plan"></span></div>
      <div class="art-row"><span class="art-label">Status</span><span class="art-value billing-status"></span></div>
      <div class="art-row"><span class="art-label">This period</span><span class="art-value billing-period"></span></div>
      <div class="art-row"><span class="art-label">Lifetime</span><span class="art-value billing-total"></span></div>
      <div class="billing-conns" style="margin-top:14px"></div>
      <div class="confirm-btns">
        <button type="button" class="btn billing-close-btn">Close</button>
      </div>
    </div>
  `;
  d.querySelector(".billing-close-btn").addEventListener("click", () => d.close());
  document.body.appendChild(d);
  _billingDialog = d;
  return d;
}

function _billingConnTable(connections) {
  const table = document.createElement("table");
  table.className = "billing-conn-table";
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  ["Connection", "Env", "Spend", "Runs"].forEach((label) => {
    const th = document.createElement("th");
    th.textContent = label;
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  const tbody = document.createElement("tbody");
  for (const c of connections) {
    const tr = document.createElement("tr");
    const cells = [
      c.label || c.connection_id || "",
      c.env_role || "",
      `$${(c.billed_usd || 0).toFixed(2)}`,
      String(c.run_count || 0),
    ];
    for (const text of cells) {
      const td = document.createElement("td");
      td.textContent = text;
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.append(thead, tbody);
  return table;
}

/**
 * Open the Billing & usage breakdown dialog: fetches GET /api/usage +
 * GET /api/usage/by-connection fresh on every open (not the cached boot-time
 * snapshot) so the numbers are current even if the sidebar row hasn't
 * refreshed yet. Never throws -- a fetch failure renders an honest message
 * in place of the plan line instead of leaving the dialog stuck on
 * "Loading…".
 */
export async function openBillingUsageDialog() {
  const d = _ensureBillingDialog();
  const gen = ++_billingGen; // claim this open's generation before any await
  const $ = (sel) => d.querySelector(sel);
  $(".billing-plan").textContent = "Loading…";
  $(".billing-status").textContent = "";
  $(".billing-period").textContent = "";
  $(".billing-total").textContent = "";
  $(".billing-conns").replaceChildren();
  d.showModal();
  try {
    const [usage, byConn] = await Promise.all([api.usage(), api.usageByConnection()]);
    // Stale-response guard: bail if a NEWER open has already claimed the
    // generation token, or the user closed this dialog before the fetch
    // resolved -- either way, this response no longer belongs on screen.
    if (gen !== _billingGen || !d.open) return;
    $(".billing-plan").textContent = usage.plan || "—";
    $(".billing-status").textContent = usage.under_free_tier ? "On the free tier" : "Free tier exceeded";
    $(".billing-period").textContent = `$${(usage.used_usd || 0).toFixed(2)} of $${(usage.allowance_usd || 0).toFixed(2)} allowance`;
    $(".billing-total").textContent = `$${(usage.total_usd || 0).toFixed(2)} all-time`;
    const conns = (byConn && byConn.connections) || [];
    const connsEl = $(".billing-conns");
    connsEl.replaceChildren(); // clear immediately before append -- belt-and-suspenders alongside the gen guard
    if (conns.length) {
      connsEl.appendChild(_billingConnTable(conns));
    } else {
      const p = document.createElement("p");
      p.style.cssText = "font-size:12px;color:var(--text-secondary);margin:0";
      p.textContent = "No connections yet.";
      connsEl.appendChild(p);
    }
  } catch (e) {
    if (gen !== _billingGen || !d.open) return; // same stale-response guard on the failure path
    $(".billing-plan").textContent = `Failed to load usage: ${e.message || e}`;
  }
}

// ---------------------------------------------------------------------------
// Usage dialog (separate <dialog>; never mutate confirmDialog)
// ---------------------------------------------------------------------------

let _usageMode = "info";        // "info" | "push"
let _usagePushTarget = null;     // { apiKind, name } when in push mode
let _onUsagePushSuccess = null;

/** Show a one-button info dialog (loading / result / error). */
export function showUsageInfo(title, html) {
  if (!refs.usageDialog) return;
  _usageMode = "info";
  _usagePushTarget = null;
  refs.usageTitle.textContent = title;
  refs.usageMessage.innerHTML = html;
  refs.usageCancel.hidden = true;
  refs.usageOk.disabled = false;
  refs.usageOk.textContent = "Close";
  if (!refs.usageDialog.open) refs.usageDialog.showModal();
}

/**
 * Show the "local-only — push to cloud?" variant. The dialog itself
 * performs the push when the user clicks OK; callers don't touch buttons.
 * `onSuccess` runs after a successful push (e.g. reload sync state).
 */
export function showUsagePushPrompt(name, apiKind, html, onSuccess) {
  if (!refs.usageDialog) return;
  _usageMode = "push";
  _usagePushTarget = { apiKind, name };
  _onUsagePushSuccess = onSuccess || null;
  refs.usageTitle.textContent = `Usage: ${name}`;
  refs.usageMessage.innerHTML = html;
  refs.usageCancel.hidden = false;
  refs.usageCancel.textContent = "Cancel";
  refs.usageOk.disabled = false;
  refs.usageOk.textContent = "Push to Cloud";
  if (!refs.usageDialog.open) refs.usageDialog.showModal();
}

export function initUsageDialog() {
  const dialog = refs.usageDialog;
  if (!dialog) return;

  refs.usageCancel?.addEventListener("click", () => {
    dialog.close();
  });

  refs.usageOk?.addEventListener("click", async () => {
    if (_usageMode === "info") {
      dialog.close();
      return;
    }
    // Push mode
    const target = _usagePushTarget;
    if (!target) { dialog.close(); return; }
    refs.usageOk.disabled = true;
    refs.usageOk.textContent = "Pushing...";
    try {
      await api.syncPush(target.apiKind, target.name);
      dialog.close();
      _onUsagePushSuccess?.();
    } catch (e) {
      refs.usageOk.disabled = false;
      refs.usageOk.textContent = "Push to Cloud";
      const errEl = document.createElement("p");
      errEl.style.color = "var(--error)";
      errEl.style.marginTop = "8px";
      errEl.textContent = `Push failed: ${e.message}`;
      refs.usageMessage.appendChild(errEl);
    }
  });

  // Reset state on close so the next open starts clean.
  dialog.addEventListener("close", () => {
    _usageMode = "info";
    _usagePushTarget = null;
    _onUsagePushSuccess = null;
    refs.usageOk.disabled = false;
    refs.usageOk.textContent = "Close";
    refs.usageCancel.hidden = true;
  });
}

// ---------------------------------------------------------------------------
// Name confirm dialog (rename before saving new script/calculation)
// ---------------------------------------------------------------------------

/**
 * Show a dialog pre-populated with a suggested name.
 * Resolves with the (possibly edited) name, or null if cancelled.
 */
export function confirmName(title, description, suggestedName) {
  return new Promise((resolve) => {
    const dialog = refs.nameConfirmDialog;
    if (!dialog) { resolve(suggestedName); return; }
    dialog._currentResolve = resolve;
    refs.nameConfirmTitle.textContent = title;
    refs.nameConfirmDesc.textContent = description;
    refs.nameConfirmInput.value = suggestedName || "";
    dialog.showModal();
    // Select all text so user can immediately type a new name
    refs.nameConfirmInput.select();
    refs.nameConfirmInput.focus();
  });
}

export function initNameConfirmDialog() {
  const dialog = refs.nameConfirmDialog;
  if (!dialog) return;

  const claim = () => { const r = dialog._currentResolve; dialog._currentResolve = null; return r; };

  refs.nameConfirmOk?.addEventListener("click", () => {
    const name = refs.nameConfirmInput.value.trim();
    if (!name) return; // don't allow empty (resolver stays live, dialog stays open)
    const r = claim();
    dialog.close();
    r?.(name);
  });

  refs.nameConfirmCancel?.addEventListener("click", () => {
    const r = claim();
    dialog.close();
    r?.(null);
  });

  dialog.addEventListener("close", () => {
    if (dialog.open) return;   // stale close from a previous dialog after a reopen
    claim()?.(null);           // Esc/backdrop: cancelled
  });

  dialog.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      refs.nameConfirmOk?.click();
    }
  });
}

// ---------------------------------------------------------------------------
// Artifact details dialog — uses the existing usageDialog modal
// ---------------------------------------------------------------------------

/**
 * Show a "More info..." dialog for one artifact.
 *  kind: "global-scripts" | "custom-calculations" | "ui-templates"
 *  name: artifact name (no extension)
 *  details: kind-specific payload — see formatters below.
 */
export function showArtifactDetailsDialog(kind, name, details) {
  let body;
  if (kind === "global-scripts")           body = _formatScriptDetails(name, details);
  else if (kind === "custom-calculations") body = _formatCalculationDetails(name, details);
  else if (kind === "ui-templates")        body = _formatTemplateDetails(name, details);
  else body = `<em>Unknown artifact kind: ${kind}</em>`;
  showUsageInfo(`Details: ${name}`, body);
}

function _row(label, value) {
  if (value == null || value === "" || (Array.isArray(value) && !value.length)) return "";
  const v = Array.isArray(value)
    ? value.map(x => typeof x === "object" ? (x.name || JSON.stringify(x)) : String(x)).join(", ")
    : String(value);
  return `<div class="art-row"><span class="art-label">${label}</span><span class="art-value">${_escHtml(v)}</span></div>`;
}

function _escHtml(s) {
  const d = document.createElement("div");
  d.textContent = String(s);
  return d.innerHTML;
}

function _formatScriptDetails(name, d) {
  // d = { meta: scriptMeta entry, usage: /api/scripts/{name}/usage response }
  const meta = d.meta || {};
  const usage = d.usage || {};
  const types = [];
  if (meta.isRest)      types.push("REST-exposed (R)");
  if (meta.eventCount)  types.push(`Event-bound (E) — ${meta.eventCount} event(s)`);
  if (meta.isModule)    types.push("Module (M)");
  return [
    _row("Type", types.length ? types.join(" · ") : "Unattached"),
    _row("Active", meta.isActive ? "Yes" : "No"),
    _row("Modified", meta.modifiedOn),
    _row("Products", usage.products),
    _row("Product attributes", (usage.productAttributes || []).map(a => `${a.key} → ${a.value}`)),
    _row("System events", usage.systemEvents),
    _row("Used by scripts", usage.scripts),
    _row("Used by calculations", usage.customQuoteCalculations),
  ].join("");
}

function _formatCalculationDetails(name, d) {
  const u = d.usage || {};
  return [
    _row("Pipeline rank", u.rank != null ? String(u.rank) : ""),
    _row("Row type", u.rowTypeLabel),
    _row("Active", u.isActive == null ? "" : (u.isActive ? "Yes" : "No")),
    _row("Executes on add/update", u.executeOnAddUpdateItem == null ? "" : (u.executeOnAddUpdateItem ? "Yes" : "No")),
    _row("Attached to fields", (u.attachedToFields || []).map(f => f.fieldName || String(f.fieldId))),
    _row("Imported modules", u.importedModules),
  ].join("");
}

function _formatTemplateDetails(name, d) {
  const m = d.meta || {};
  const sys = `${name}.system.html`;
  return [
    _row("Page", m.pageName),
    _row("Slot", m.slotName),
    _row("State", m.isSystem ? "System reference (read-only)"
                  : m.isOverride ? "Customizes a system default"
                  : "Pure custom"),
    _row("System equivalent", m.isOverride ? sys : ""),
    _row("Modified", m.modifiedOn),
    _row("Modified by", m.modifiedBy),
  ].join("");
}

// ---------------------------------------------------------------------------
// Template picker dialog (select a template to upload to)
// ---------------------------------------------------------------------------

let _selectedTemplateId = null;

/**
 * Show the template picker. Resolves with {id, name} or null if cancelled.
 */
export function showTemplatePicker(templates) {
  return new Promise((resolve) => {
    const dialog = refs.templatePickerDialog;
    if (!dialog) { resolve(null); return; }
    dialog._currentResolve = resolve;
    _selectedTemplateId = null;

    // Populate the list
    const list = refs.templatePickerList;
    list.innerHTML = "";
    for (const t of templates) {
      const card = document.createElement("div");
      card.className = "template-card";
      card.dataset.id = t.id;
      card.dataset.name = t.name || "";
      card.innerHTML = `
        <div class="template-card-name">${_escHtml(t.name || "(unnamed)")}</div>
        <div class="template-card-meta">${_escHtml(t.type || "Template")} &middot; ${_escHtml(t.filename || "")}</div>
      `;
      card.addEventListener("click", () => {
        list.querySelectorAll(".template-card").forEach(c => c.classList.remove("selected"));
        card.classList.add("selected");
        _selectedTemplateId = t.id;
        refs.templatePickerOk.disabled = false;
      });
      card.addEventListener("dblclick", () => {
        _selectedTemplateId = t.id;
        _finishTemplatePicker(dialog, t.id, t.name);
      });
      list.appendChild(card);
    }

    refs.templatePickerOk.disabled = true;
    dialog.showModal();
  });
}

function _finishTemplatePicker(dialog, id, name) {
  const r = dialog._currentResolve;
  dialog._currentResolve = null;
  dialog.close();
  r?.({ id, name });
  _selectedTemplateId = null;
}

export function initTemplatePickerDialog() {
  const dialog = refs.templatePickerDialog;
  if (!dialog) return;

  const claim = () => { const r = dialog._currentResolve; dialog._currentResolve = null; return r; };

  refs.templatePickerOk?.addEventListener("click", () => {
    if (!_selectedTemplateId) return;
    const card = refs.templatePickerList.querySelector(`.template-card[data-id="${_selectedTemplateId}"]`);
    const name = card?.dataset.name || "";
    _finishTemplatePicker(dialog, _selectedTemplateId, name);
  });

  refs.templatePickerCancel?.addEventListener("click", () => {
    const r = claim();
    dialog.close();
    r?.(null);
  });

  dialog.addEventListener("close", () => {
    if (dialog.open) return;   // stale close from a previous dialog after a reopen
    claim()?.(null);           // Esc/backdrop: cancelled
  });
}
